import { useEffect, useState } from "react";
import { Copy, Gift, Heart, Plus, PlusMinus, QrCode, Trash, UserPlus, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";

const normLivelli = (cfg) => {
  const src = (cfg.livelli || []).length ? cfg.livelli : [{
    nome: cfg.nome_programma || "Base", colore: cfg.colore || "#18181b",
    da_punti: 0, punti_per_visita: cfg.punti_per_visita || 1, premi: cfg.soglie || [],
  }];
  return src.map((l, i) => ({
    nome: "", colore: "#18181b", da_punti: i === 0 ? 0 : 100,
    punti_per_visita: 1, premi: [], ...l,
  }));
};

export default function Fidelity() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mov, setMov] = useState(null); // {customer_id?, customer_name?} | {nuovo: true}
  const [movPunti, setMovPunti] = useState("");
  const [movMotivo, setMovMotivo] = useState("");
  const [movQuery, setMovQuery] = useState("");
  const [movRisultati, setMovRisultati] = useState([]);
  const [card, setCard] = useState(null); // {customer_id, customer_name, phone, punti, url}
  const [join, setJoin] = useState(false);

  const load = () => api.get("/fidelity/classifica").then((r) => {
    setData(r.data);
    const c = r.data.config;
    setCfg({ ...c, nome_programma: c.nome_programma || "", colore: c.colore || "#18181b", livelli: normLivelli(c) });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!movQuery.trim()) { setMovRisultati([]); return; }
    const t = setTimeout(() => {
      api.get(`/customers?q=${encodeURIComponent(movQuery)}`)
        .then((r) => setMovRisultati((r.data || []).slice(0, 6))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [movQuery]);

  const save = async () => {
    setSaving(true);
    try {
      const livelli = cfg.livelli
        .map((l) => ({ ...l, da_punti: parseInt(l.da_punti) || 0, punti_per_visita: parseInt(l.punti_per_visita) || 1 }))
        .sort((a, b) => a.da_punti - b.da_punti);
      livelli[0].da_punti = 0; // il livello base parte sempre da zero
      const payload = {
        ...cfg, livelli,
        punti_per_visita: livelli[0].punti_per_visita, // sync campi legacy
        soglie: livelli[0].premi,
      };
      await api.patch("/settings", { fidelity: payload });
      toast.success("Programma fidelity salvato");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const openMov = (row) => {
    setMov(row || { nuovo: true });
    setMovPunti("");
    setMovMotivo("");
    setMovQuery("");
    setMovRisultati([]);
  };

  const sendMov = async () => {
    const punti = parseInt(movPunti, 10);
    if (!punti) { toast.error("Inserisci i punti (es. 50 oppure -30 per un riscatto)"); return; }
    if (!mov.customer_id) { toast.error("Seleziona il cliente"); return; }
    try {
      const r = await api.post("/fidelity/movimento", {
        customer_id: mov.customer_id, punti, motivo: movMotivo,
      });
      toast.success(`Punti aggiornati: nuovo saldo ${r.data.saldo}`);
      setMov(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const riscatta = (row) => openMov({ ...row, _riscatto: true });

  const openCard = async (row) => {
    try {
      const r = await api.get(`/fidelity/card/${row.customer_id}`);
      setCard({ ...row, url: r.data.url, punti: r.data.saldo, justCreated: r.data.just_created });
    } catch (err) { toast.error(apiError(err)); }
  };

  const azzeraCard = async () => {
    try {
      await api.post("/fidelity/movimento", {
        customer_id: card.customer_id, punti: -card.punti,
        motivo: "Azzeramento punti pregressi (attivazione card)",
      });
      toast.success("Punti azzerati: la card parte da zero");
      setCard({ ...card, punti: 0, justCreated: false });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!data || !cfg) return <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>;

  const setLivello = (i, k, v) =>
    setCfg({ ...cfg, livelli: cfg.livelli.map((l, j) => (j === i ? { ...l, [k]: v } : l)) });
  const setPremio = (li, pi, k, v) =>
    setCfg({
      ...cfg,
      livelli: cfg.livelli.map((l, j) => (j === li
        ? { ...l, premi: l.premi.map((p, q) => (q === pi ? { ...p, [k]: v } : p)) } : l)),
    });

  return (
    <div className="space-y-5 max-w-4xl" data-testid="fidelity-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Fidelity</h1>
          <p className="text-xs text-zinc-500">Programma fedeltà a livelli: i punti si accumulano a ogni visita completata (OUT), il cliente sale di livello in automatico.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setJoin(true)} data-testid="fid-join-btn"
            className="h-11 px-4 rounded-md border border-zinc-300 bg-white text-zinc-700 text-xs font-bold flex items-center gap-1.5 hover:bg-zinc-50">
            <UserPlus size={15} weight="bold" /> Modulo iscrizione
          </button>
          <button onClick={() => openMov(null)} data-testid="fid-assegna-punti"
            className="h-11 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
            <PlusMinus size={15} weight="bold" /> Assegna / riscatta punti
          </button>
        </div>
      </div>

      <section className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4" data-testid="fidelity-config">
        <label className="flex items-center gap-2 text-sm text-zinc-700 font-semibold">
          <input type="checkbox" checked={!!cfg.attivo} onChange={(e) => setCfg({ ...cfg, attivo: e.target.checked })}
            className="w-4 h-4 accent-zinc-900" data-testid="fid-attivo" /> Programma fidelity attivo
        </label>
        <label className="flex items-start gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={!!cfg.azzeramento_iscrizione}
            onChange={(e) => setCfg({ ...cfg, azzeramento_iscrizione: e.target.checked })}
            className="w-4 h-4 mt-0.5 accent-zinc-900" data-testid="fid-azzeramento" />
          <span>All'iscrizione online <strong>azzera i punti</strong> accumulati prima dell'attivazione della card
            <span className="block text-[11px] text-zinc-400 font-normal">Logica "card reale": il cliente parte da zero quando riceve la card. Se disattivato, i punti pregressi vengono mantenuti.</span>
          </span>
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nome del programma (sulla card)</label>
            <input value={cfg.nome_programma} onChange={(e) => setCfg({ ...cfg, nome_programma: e.target.value })}
              placeholder="es. Ninfa Club" className={inputCls} data-testid="fid-nome-programma" />
          </div>
          <div>
            <label className={labelCls}>Colore card (predefinito)</label>
            <input type="color" value={cfg.colore} onChange={(e) => setCfg({ ...cfg, colore: e.target.value })}
              className="h-11 w-full rounded-md border border-zinc-300 bg-white p-1 cursor-pointer"
              data-testid="fid-colore" />
          </div>
        </div>

        <div className="space-y-3">
          <label className={labelCls}>Livelli e premi</label>
          {cfg.livelli.map((l, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-4 space-y-3" data-testid={`fid-livello-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="color" value={l.colore} onChange={(e) => setLivello(i, "colore", e.target.value)}
                  className="h-11 w-14 rounded-md border border-zinc-300 bg-white p-1 cursor-pointer shrink-0"
                  data-testid={`fid-livello-${i}-colore`} title="Colore card del livello" />
                <input value={l.nome} onChange={(e) => setLivello(i, "nome", e.target.value)}
                  placeholder="Nome livello (es. Gold)" className={`${inputCls} flex-1 min-w-32`}
                  data-testid={`fid-livello-${i}-nome`} />
                {cfg.livelli.length > 1 && (
                  <button onClick={() => setCfg({ ...cfg, livelli: cfg.livelli.filter((_, j) => j !== i) })}
                    className="h-11 w-11 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center shrink-0"
                    data-testid={`fid-livello-${i}-delete`}><Trash size={16} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Da punti {i === 0 && "(sempre 0)"}</label>
                  <input type="number" min="0" value={l.da_punti} disabled={i === 0}
                    onChange={(e) => setLivello(i, "da_punti", e.target.value)}
                    className={`${inputCls} ${i === 0 ? "bg-zinc-100" : ""}`}
                    data-testid={`fid-livello-${i}-da-punti`} />
                </div>
                <div>
                  <label className={labelCls}>Punti per visita</label>
                  <input type="number" min="1" value={l.punti_per_visita}
                    onChange={(e) => setLivello(i, "punti_per_visita", e.target.value)}
                    className={inputCls} data-testid={`fid-livello-${i}-ppv`} />
                </div>
              </div>
              <div className="space-y-2">
                <label className={labelCls}>Premi del livello (valore in punti)</label>
                {l.premi.map((p, pi) => (
                  <div key={pi} className="flex items-center gap-2" data-testid={`fid-livello-${i}-premio-${pi}`}>
                    <input type="number" min="1" value={p.punti}
                      onChange={(e) => setPremio(i, pi, "punti", parseInt(e.target.value) || 0)}
                      className={`${inputCls} w-28`} data-testid={`fid-livello-${i}-premio-${pi}-punti`} />
                    <input value={p.premio} onChange={(e) => setPremio(i, pi, "premio", e.target.value)}
                      placeholder="Premio (es. Dessert omaggio)" className={inputCls}
                      data-testid={`fid-livello-${i}-premio-${pi}-premio`} />
                    <button onClick={() => setPremio(i, pi, "_del", true) || setCfg({
                        ...cfg,
                        livelli: cfg.livelli.map((x, j) => (j === i ? { ...x, premi: x.premi.filter((_, q) => q !== pi) } : x)),
                      })}
                      className="h-11 w-11 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center shrink-0"
                      data-testid={`fid-livello-${i}-premio-${pi}-delete`}><Trash size={16} /></button>
                  </div>
                ))}
                <button onClick={() => setCfg({
                    ...cfg,
                    livelli: cfg.livelli.map((x, j) => (j === i ? { ...x, premi: [...x.premi, { punti: 100, premio: "" }] } : x)),
                  })}
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1.5"
                  data-testid={`fid-livello-${i}-add-premio`}><Plus size={14} weight="bold" /> Aggiungi premio</button>
              </div>
            </div>
          ))}
          <button onClick={() => setCfg({
              ...cfg,
              livelli: [...cfg.livelli, {
                nome: "", colore: "#b45309",
                da_punti: Math.max(0, ...cfg.livelli.map((x) => parseInt(x.da_punti) || 0)) + 100,
                punti_per_visita: 1, premi: [],
              }],
            })}
            className="h-10 px-4 rounded-md border border-dashed border-zinc-400 text-xs font-bold text-zinc-600 flex items-center gap-1.5 hover:bg-zinc-50"
            data-testid="fid-add-livello"><Plus size={14} weight="bold" /> Aggiungi livello</button>
        </div>

        <button onClick={save} disabled={saving} data-testid="fid-save"
          className="h-11 px-6 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50">
          Salva programma
        </button>
      </section>

      <section className="bg-white border border-zinc-200 rounded-lg" data-testid="fidelity-classifica">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Heart size={18} weight="fill" className="text-rose-500" />
          <h2 className="font-display font-semibold text-zinc-900">Clienti e saldo punti</h2>
        </div>
        <div className="divide-y divide-zinc-100">
          {data.classifica.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessuna visita completata ancora.</div>
          )}
          {data.classifica.map((r, i) => (
            <div key={r.customer_id} className="flex items-center gap-3 px-5 py-3" data-testid={`fid-row-${r.customer_id}`}>
              <span className="w-6 text-sm font-black text-zinc-400">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">{r.customer_name || "—"}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                  {r.visite} visite
                  {r.livello && (
                    <span className="text-[10px] font-black uppercase tracking-wide px-1.5 py-px rounded-full border"
                      style={{ color: r.livello.colore, borderColor: r.livello.colore }}
                      data-testid={`fid-livello-badge-${r.customer_id}`}>{r.livello.nome}</span>
                  )}
                </div>
              </div>
              {r.premio_raggiunto && (
                <button onClick={() => riscatta(r)} data-testid={`fid-riscatta-${r.customer_id}`}
                  title={`Riscatta "${r.premio_raggiunto}" (${r.premio_punti} punti)`}
                  className="h-9 px-3 rounded-md bg-emerald-600 text-white text-[11px] font-bold flex items-center gap-1.5 hover:bg-emerald-700 active:scale-95 transition">
                  <Gift size={14} weight="bold" /> RISCATTA
                </button>
              )}
              <button onClick={() => openCard(r)} data-testid={`fid-card-${r.customer_id}`}
                className="h-9 w-9 rounded-md border border-amber-300 bg-amber-50 text-amber-700 flex items-center justify-center hover:bg-amber-100"
                title="Card fidelity digitale (QR + link)">
                <QrCode size={15} weight="bold" />
              </button>
              <button onClick={() => openMov(r)} data-testid={`fid-mov-${r.customer_id}`}
                className="h-9 w-9 rounded-md border border-zinc-300 text-zinc-600 flex items-center justify-center hover:bg-zinc-50"
                title="Assegna o togli punti">
                <PlusMinus size={15} weight="bold" />
              </button>
              <div className="text-right w-16">
                <div className="text-sm font-black text-zinc-900">{r.punti} pt</div>
                {r.premio_raggiunto && (
                  <div className="text-[10px] font-bold text-emerald-700 truncate">{r.premio_raggiunto}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {join && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => setJoin(false)} data-testid="fid-join-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-zinc-900">Modulo iscrizione fidelity</h3>
                <p className="text-sm text-zinc-500 mt-0.5">I clienti si iscrivono da soli e ricevono subito la card</p>
              </div>
              <button onClick={() => setJoin(false)} data-testid="fid-join-close"
                className="text-zinc-400 hover:text-zinc-700 p-1"><X size={16} weight="bold" /></button>
            </div>
            <div className="flex justify-center">
              <img src={data.join_qr_url} alt="QR modulo iscrizione"
                className="w-44 h-44 rounded-lg border border-zinc-200 bg-white p-2" data-testid="fid-join-qr" />
            </div>
            <p className="text-xs text-zinc-500 text-center">Stampa il QR per i tavoli o condividi il link: chi si iscrive riceve subito la sua card digitale.</p>
            <p className="text-[11px] text-zinc-400 break-all text-center" data-testid="fid-join-url">{data.join_url}</p>
            <button onClick={() => { navigator.clipboard.writeText(data.join_url); toast.success("Link copiato"); }}
              data-testid="fid-join-copy"
              className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-zinc-800 active:scale-95 transition">
              <Copy size={15} weight="bold" /> Copia link
            </button>
          </div>
        </div>
      )}

      {card && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => setCard(null)} data-testid="fid-card-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-zinc-900">Card fidelity digitale</h3>
                <p className="text-sm text-zinc-500 mt-0.5">{card.customer_name} — {card.punti} pt</p>
              </div>
              <button onClick={() => setCard(null)} data-testid="fid-card-close"
                className="text-zinc-400 hover:text-zinc-700 p-1"><X size={16} weight="bold" /></button>
            </div>
            <div className="flex justify-center">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/fidelity/${card.url.split("/").pop()}/qr.png`}
                alt="QR fidelity card" className="w-44 h-44 rounded-lg border border-zinc-200 bg-white p-2"
                data-testid="fid-card-qr" />
            </div>
            {card.justCreated && card.punti > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="fid-card-azzera-box">
                <p className="text-xs text-amber-900 font-semibold">
                  Prima attivazione: {card.customer_name} ha già {card.punti} punti accumulati prima della card. Cosa vuoi fare?
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setCard({ ...card, justCreated: false })} data-testid="fid-card-mantieni"
                    className="flex-1 h-9 rounded-md bg-white border border-zinc-300 text-zinc-700 text-xs font-bold hover:bg-zinc-50">
                    Mantieni {card.punti} punti
                  </button>
                  <button onClick={azzeraCard} data-testid="fid-card-azzera"
                    className="flex-1 h-9 rounded-md bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 active:scale-95 transition">
                    Azzera: parti da 0
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-zinc-500 text-center">Fai inquadrare il QR al cliente, oppure inviagli il link: la card si apre nel suo browser e mostra saldo, premi e QR da esibire in cassa.</p>
            <p className="text-[11px] text-zinc-400 break-all text-center" data-testid="fid-card-url">{card.url}</p>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(card.url); toast.success("Link copiato"); }}
                data-testid="fid-card-copy"
                className="flex-1 h-11 rounded-md bg-zinc-900 text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-zinc-800 active:scale-95 transition">
                <Copy size={15} weight="bold" /> Copia link
              </button>
              {card.phone && (
                <a href={`https://wa.me/${String(card.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Ecco la tua fidelity card digitale: ${card.url}`)}`}
                  target="_blank" rel="noreferrer" data-testid="fid-card-wa"
                  className="flex-1 h-11 rounded-md bg-emerald-600 text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:bg-emerald-700 active:scale-95 transition">
                  <WhatsappLogo size={15} weight="bold" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {mov && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => setMov(null)} data-testid="fid-mov-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900">
                {mov._riscatto ? `Riscatta: ${mov.premio_raggiunto}` : "Assegna / riscatta punti"}
              </h3>
              {mov.customer_name && (
                <p className="text-sm text-zinc-500 mt-0.5">{mov.customer_name} — saldo attuale {mov.punti} pt</p>
              )}
            </div>
            {!mov.customer_id && (
              <div className="relative">
                <label className={labelCls}>Cliente</label>
                <input value={movQuery} onChange={(e) => setMovQuery(e.target.value)}
                  placeholder="Cerca per nome o telefono..." className={inputCls} data-testid="fid-mov-search" />
                {movRisultati.length > 0 && (
                  <div className="absolute z-10 inset-x-0 top-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg divide-y divide-zinc-100"
                    data-testid="fid-mov-results">
                    {movRisultati.map((c) => (
                      <button key={c.id} type="button"
                        onClick={() => { setMov({ ...mov, customer_id: c.id, customer_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(), punti: c.fidelity_points || 0 }); setMovQuery(""); setMovRisultati([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50"
                        data-testid={`fid-mov-pick-${c.id}`}>
                        <span className="font-semibold">{c.first_name} {c.last_name}</span>
                        <span className="text-zinc-400 text-xs ml-2">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
                {mov.customer_id && <p className="text-xs text-emerald-700 mt-1 font-semibold">✓ {mov.customer_name}</p>}
              </div>
            )}
            <div>
              <label className={labelCls}>Punti {mov._riscatto ? "(saranno negativi)" : "(negativi = riscatto)"}</label>
              <input type="number" value={mov._riscatto ? -(mov.premio_punti || 0) : movPunti}
                disabled={!!mov._riscatto}
                onChange={(e) => setMovPunti(e.target.value)}
                placeholder="es. 50 oppure -30" className={`${inputCls} ${mov._riscatto ? "bg-zinc-100" : ""}`}
                data-testid="fid-mov-punti" />
            </div>
            <div>
              <label className={labelCls}>Motivo</label>
              <input value={mov._riscatto ? `Riscatto premio: ${mov.premio_raggiunto}` : movMotivo}
                disabled={!!mov._riscatto}
                onChange={(e) => setMovMotivo(e.target.value)}
                placeholder="es. Bonus benvenuto" className={`${inputCls} ${mov._riscatto ? "bg-zinc-100" : ""}`}
                data-testid="fid-mov-motivo" />
            </div>
            <div className="flex gap-2">
              <button
                onClick={mov._riscatto
                  ? async () => {
                      try {
                        const r = await api.post("/fidelity/movimento", {
                          customer_id: mov.customer_id, punti: -(mov.premio_punti || 0),
                          motivo: `Riscatto premio: ${mov.premio_raggiunto}`,
                        });
                        toast.success(`Premio riscattato! Nuovo saldo ${r.data.saldo} pt`);
                        setMov(null);
                        load();
                      } catch (err) { toast.error(apiError(err)); }
                    }
                  : sendMov}
                data-testid="fid-mov-send"
                className="flex-1 h-11 rounded-md bg-zinc-900 text-white text-sm font-bold hover:bg-zinc-800 active:scale-95 transition">
                {mov._riscatto ? "CONFERMA RISCATTO" : "SALVA MOVIMENTO"}
              </button>
              <button onClick={() => setMov(null)} data-testid="fid-mov-cancel"
                className="h-11 px-4 rounded-md border border-zinc-300 text-zinc-600 text-sm font-semibold">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
